import React, { MutableRefObject, useCallback, useEffect, useReducer, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { errorToast, infoToast, shortAddress, successToast } from '../../util/Util'
import ConfirmButtonModal from '../../components/ConfirmButtonModal'
import Dots from '../../icons/Dots'
import TimeText from '../../components/TimeText'
import { SelectedMethodIcon } from '../../components/PaymentMethodDisplay'
import { generateRoundFlagIconFromCountryCode } from '../../util/Flags'
import { getMethod } from '../../util/Managed'
import { EmptyState } from '../../components/DashboardPrimitives'
import Radom, { ExternalAccount } from '../../state/Radom'
import Spinner from '../../components/Spinner'
import { SecondaryButton } from '../../components/Button'
import { isCryptoExternalAccount, isEurIbanExternalAccount } from '../../util/ExternalAccounts'
import Gate from '../../components/Gate'

interface CustomerPayoutsAccountsProps {
  customerId: string
  isLoading: boolean
  onRefreshRef?: MutableRefObject<(() => void) | null>
  onCreateAccount?: () => void
  /** Leave the Add account button to the surrounding page. */
  hideToolbar?: boolean
}

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
`

const GroupLabel = styled.div`
  color: var(--text-muted, #6b7280);
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding-top: 4px;
  text-transform: uppercase;
`

const AccountList = styled.div`
  display: flex;
  flex-direction: column;
`

const AccountRow = styled.div`
  align-items: center;
  border-top: 1px solid var(--border-soft, #eceff3);
  display: flex;
  gap: 16px;
  justify-content: space-between;
  min-width: 0;
  padding: 12px 4px;

  &:first-child {
    border-top: 0;
  }
`

const AccountMain = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const AccountTitle = styled.div`
  color: var(--ink, #20242d);
  font-size: 14px;
  font-weight: 500;
`

const AccountDetails = styled.div`
  color: var(--text-soft, #7d8492);
  font-size: 13px;
`

const AccountIcon = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  height: 36px;
  justify-content: center;
  width: 36px;
`

const AccountLead = styled.div`
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  gap: 14px;
  min-width: 0;
`

const AccountMeta = styled.div`
  color: var(--text-soft, #7d8492);
  font-size: 12px;
`

const AccountActions = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
`

// Send delivers to wallets and EUR IBANs today; other account types stay
// listed without the shortcut rather than leading to a dead end.
const canSendTo = (account: ExternalAccount): boolean =>
  isCryptoExternalAccount(account) || isEurIbanExternalAccount(account)

const accountTitle = (account: ExternalAccount): string => {
  if (account.crypto) {
    const method = getMethod(account.crypto.network)
    return `${method?.name ?? account.crypto.network} wallet`
  }
  if (account.iban) return `${(account.currency || 'EUR').toUpperCase()} account ··${account.iban.last4}`
  if (account.account) return `USD account ··${account.account.last4}`
  if (account.gbAccount) return `GBP account ··${account.gbAccount.last4}`
  if (account.clabe) return `CLABE ··${account.clabe.last4}`
  if (account.swift) return `SWIFT ··${account.swift.last4}`
  return `${(account.currency || '').toUpperCase()} ${String(account.accountType)} account`.trim()
}

const accountDetail = (account: ExternalAccount): string => {
  const owner = account.accountOwnerName?.trim()
  if (account.crypto) return [owner, shortAddress(account.crypto.address, 6)].filter(Boolean).join(' · ')
  if (account.iban) return [owner, account.iban.bic, account.iban.country].filter(Boolean).join(' · ')
  if (account.account) {
    return [owner, `Routing ${account.account.routingNumber}`, account.account.checkingOrSavings]
      .filter(Boolean).join(' · ')
  }
  if (account.gbAccount) return [owner, `Sort code ${account.gbAccount.sortCode}`].filter(Boolean).join(' · ')
  if (account.swift) return [owner, account.swift.bic, account.swift.country].filter(Boolean).join(' · ')
  return owner ?? ''
}

const accountIcon = (account: ExternalAccount): JSX.Element | null => {
  if (account.crypto) {
    const method = getMethod(account.crypto.network)
    return method ? <SelectedMethodIcon method={method} size={28} disableToolTip /> : null
  }
  return generateRoundFlagIconFromCountryCode(account.account ? 'US' : account.gbAccount ? 'GB' : 'EU', 28)
}

const CustomerPayoutsAccounts = ({
  customerId,
  isLoading: parentLoading,
  onRefreshRef,
  onCreateAccount,
  hideToolbar
}: CustomerPayoutsAccountsProps): JSX.Element => {
  const navigate = useNavigate()
  const [closeDropdownFns, updateCloseDropdownFns] = useReducer((p: any, n: any) => ({ ...p, ...n }), {})
  const [externalAccounts, setExternalAccounts] = useState<ExternalAccount[]>([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)

  const fetchAccounts = useCallback(async () => {
    if (!customerId) return
    setIsLoadingAccounts(true)
    try {
      const accounts = await Radom.listExternalAccounts(customerId)
      setExternalAccounts(accounts)
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message || 'Failed to load accounts')
    }
    setIsLoadingAccounts(false)
  }, [customerId])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  // Expose refresh function to parent via ref
  useEffect(() => {
    if (onRefreshRef) {
      onRefreshRef.current = fetchAccounts
    }
    return () => {
      if (onRefreshRef) {
        onRefreshRef.current = null
      }
    }
  }, [onRefreshRef, fetchAccounts])

  const handleDeleteAccount = async (accountId: string): Promise<void> => {
    try {
      await Radom.deleteExternalAccount(accountId)
      successToast('Account deleted')
      fetchAccounts()
    } catch (err: any) {
      errorToast(err.response?.data?.error || err.message || 'Failed to delete account')
    }
  }

  const isLoading = parentLoading || isLoadingAccounts

  if (isLoading) {
    return <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
      <Spinner />
    </div>
  }

  const renderAccount = (account: ExternalAccount): JSX.Element => (
    <AccountRow key={account.id}>
      <AccountLead>
        <AccountIcon>{accountIcon(account)}</AccountIcon>
        <AccountMain>
          <AccountTitle>{accountTitle(account)}</AccountTitle>
          <AccountDetails>{accountDetail(account)}</AccountDetails>
          <AccountMeta>Added <TimeText date={new Date(account.createdAt)} /></AccountMeta>
        </AccountMain>
      </AccountLead>
      <AccountActions onClick={e => e.stopPropagation()}>
        {canSendTo(account) &&
          <Gate permission='WritePayouts'>
            <SecondaryButton
              style={{ minHeight: 34, padding: '0 14px', fontSize: 13 }}
              onClick={() => navigate(`/send?customer=${customerId}&account=${account.id}`)}>
              Send funds
            </SecondaryButton>
          </Gate>}
        <Dropdown
          noChevron
          onCloseFn={f => updateCloseDropdownFns({ [account.id]: f })}
          iconTrigger
          selectedContent={<Dots width={15} />}
          dropdownContent={
            <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
              <DropdownItem onClick={() => {
                navigator.clipboard.writeText(account.id)
                infoToast('Account ID copied to clipboard')
                closeDropdownFns[account.id]?.()
              }}>Copy account ID</DropdownItem>
              <Gate permission='WriteExternalAccount'>
                <ConfirmButtonModal
                  title='Delete account'
                  description='Are you sure you want to delete this account?'
                  action='Delete account'
                  onConfirm={async () => { await handleDeleteAccount(account.id) }}
                  ActionComponent={(props: any) => <DropdownItem {...props}>Delete account</DropdownItem>}
                />
              </Gate>
            </div>
          }
        />
      </AccountActions>
    </AccountRow>
  )

  if (externalAccounts.length === 0) {
    return (
      <EmptyState style={{ flexDirection: 'column', gap: 14, minHeight: 96 }}>
        <span>No bank account or wallet saved for this contact yet.</span>
        {onCreateAccount && (
          <Gate permission='WriteExternalAccount'>
            <SecondaryButton onClick={onCreateAccount}>Add account</SecondaryButton>
          </Gate>
        )}
      </EmptyState>
    )
  }

  const cryptoAccounts = externalAccounts.filter(isCryptoExternalAccount)
  const fiatAccounts = externalAccounts.filter(a => !isCryptoExternalAccount(a))
  // Group labels only earn their place when there is more than one group.
  const showGroupLabels = cryptoAccounts.length > 0 && fiatAccounts.length > 0

  return (
    <>
      {onCreateAccount && !hideToolbar && (
        <Gate permission='WriteExternalAccount'>
          <Toolbar>
            <SecondaryButton style={{ minHeight: 34, padding: '0 14px', fontSize: 13 }} onClick={onCreateAccount}>
              Add account
            </SecondaryButton>
          </Toolbar>
        </Gate>
      )}
      {fiatAccounts.length > 0 && (
        <>
          {showGroupLabels && <GroupLabel>Bank accounts</GroupLabel>}
          <AccountList>{fiatAccounts.map(renderAccount)}</AccountList>
        </>
      )}
      {cryptoAccounts.length > 0 && (
        <>
          {showGroupLabels && <GroupLabel>Wallets</GroupLabel>}
          <AccountList>{cryptoAccounts.map(renderAccount)}</AccountList>
        </>
      )}
    </>
  )
}

export default CustomerPayoutsAccounts
